import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, Legend } from "recharts";
import { Download, FileDown, Warehouse as WhIcon, Factory, RotateCcw, Trophy, TrendingUp, Users as UsersIcon, Truck, FileText } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { HseExpiringWidget } from "@/components/HseExpiringWidget";
import { QuarterlySalesChart } from "@/components/QuarterlySalesChart";

const TR_MONTHS = ["Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];
function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function buildMonthOptions(count = 12) {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    opts.push({ key, label: `${TR_MONTHS[d.getMonth()]} ${d.getFullYear()}` });
  }
  return opts;
}

function Stat({ label, value, testid, tone = "default", sub }) {
  const toneCls = tone === "warehouse" ? "border-l-[#0055FF]" : tone === "production" ? "border-l-[#FF3B30]" : "border-l-[#09090B]";
  return (
    <div className={`bg-white border-2 border-[#09090B] border-l-[6px] ${toneCls} p-4 min-w-0 overflow-hidden`} data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 truncate">{label}</div>
      <div className="mt-2 text-[18pt] font-mono tracking-tight font-bold tabular-nums break-words leading-tight">{value}</div>
      {sub && <div className="mt-1 text-xs text-neutral-500 min-w-0">{sub}</div>}
    </div>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

function ChangeBadge({ current, previous, label, testid }) {
  if (previous == null || current == null) return null;
  const prev = Number(previous) || 0;
  const curr = Number(current) || 0;
  if (prev === 0 && curr === 0) return null;
  let pct = null;
  let arrow = "→";
  if (prev === 0) {
    // 0'dan artış — % gösteremeyiz, "yeni" olarak işaretle
    return (
      <span
        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 border border-emerald-600 bg-emerald-50 text-emerald-700 text-[9px] font-mono font-bold uppercase whitespace-nowrap"
        data-testid={testid}
        title={`${label}: geçen dönem 0 idi`}
      >
        ↑ yeni
      </span>
    );
  }
  pct = ((curr - prev) / Math.abs(prev)) * 100;
  const rounded = Math.abs(pct) >= 100 ? pct.toFixed(0) : pct.toFixed(1);
  if (pct > 0) arrow = "↑";
  else if (pct < 0) arrow = "↓";
  const tone = pct > 0
    ? "border-emerald-600 bg-emerald-50 text-emerald-700"
    : pct < 0
    ? "border-red-600 bg-red-50 text-red-700"
    : "border-neutral-400 bg-neutral-50 text-neutral-600";
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 border ${tone} text-[9px] font-mono font-bold uppercase whitespace-nowrap`}
      data-testid={testid}
      title={`${label}: geçen dönem ${fmtCurrency(prev)}`}
    >
      {arrow} {pct > 0 ? "+" : ""}{rounded}%
    </span>
  );
}

function TopProductsCard({ kicker, title, items, emptyLabel, testid, period }) {
  const download = async () => {
    try {
      const res = await api.get(`/reports/top-products/xlsx?period=${period}`, { responseType: "blob" });
      const dispo = res.headers?.["content-disposition"] || "";
      const match = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const fallback = period === "year" ? "yillik-satis-adetleri.xlsx" : "aylik-satis-adetleri.xlsx";
      const fname = match ? decodeURIComponent(match[1]) : fallback;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = fname; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      // toast import already exists elsewhere but keep silent to avoid crash if missing
      console.error("top-products xlsx failed", e);
    }
  };

  return (
    <div className="bg-white border-2 border-[#09090B] p-6" data-testid={`${testid}-card`}>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Trophy size={20} className="text-[#0055FF] shrink-0" />
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">{kicker}</div>
            <div className="text-lg font-bold tracking-tight truncate">{title}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={download}
          disabled={items.length === 0}
          className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#09090B] font-mono uppercase text-[10px] tracking-widest hover:bg-[#09090B] hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-xlsx-btn`}
        >
          <FileDown size={14} /> Excel İndir
        </button>
      </div>
      {items.length === 0 ? (
        <div className="text-sm font-mono text-neutral-500 py-6 text-center" data-testid={`${testid}-empty`}>{emptyLabel}</div>
      ) : (
        <div className="divide-y divide-neutral-200 max-h-[420px] overflow-y-auto">
          {items.map((p, i) => (
            <div key={`${p.product_name}-${i}`} className="flex items-center gap-3 py-2.5" data-testid={`${testid}-row-${i}`}>
              <div className={`w-7 h-7 flex items-center justify-center font-mono font-bold text-xs border-2 border-[#09090B] shrink-0 ${i === 0 ? "bg-[#0055FF] text-white" : "bg-white"}`}>
                {i + 1}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate" data-testid={`${testid}-name-${i}`} title={p.product_name}>{p.product_name}</div>
                <div className="text-[10px] font-mono text-neutral-500">{p.order_count} sipariş</div>
              </div>
              <div className="text-right font-mono text-sm font-bold whitespace-nowrap" data-testid={`${testid}-qty-${i}`}>
                {Number(p.quantity).toLocaleString("tr-TR")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



export default function Dashboard() {
  const { user } = useAuth();
  const isPrivileged = user && (user.role === "admin" || user.role === "sub_admin");
  const canSeeDailyQuotes = isPrivileged || user?.role === "sales";
  const monthOptions = useMemo(() => buildMonthOptions(12), []);
  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topProductsMonth, setTopProductsMonth] = useState([]);
  const [topProductsYear, setTopProductsYear] = useState([]);
  const [pendingWaybillCustomers, setPendingWaybillCustomers] = useState([]);
  const [pwcSelected, setPwcSelected] = useState(null); // {customer_id, customer_name}
  const [pwcBreakdown, setPwcBreakdown] = useState(null);
  const [pwcLoading, setPwcLoading] = useState(false);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [financeSummary, setFinanceSummary] = useState(null);
  const [dailyQuotes, setDailyQuotes] = useState(null);
  const [topMonth, setTopMonth] = useState(currentMonthKey());
  const [perfMonth, setPerfMonth] = useState(currentMonthKey());
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [detailedPdf, setDetailedPdf] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Build the query string that both the chart and the export buttons use.
  // When both dates are set → explicit range. Otherwise fall back to rolling `days`.
  const query = useMemo(() => {
    if (startDate && endDate) return `start_date=${startDate}&end_date=${endDate}`;
    if (startDate) return `start_date=${startDate}`;
    if (endDate) return `end_date=${endDate}&days=${days}`;
    return `days=${days}`;
  }, [days, startDate, endDate]);

  const rangeLabel = useMemo(() => {
    if (startDate && endDate) return `${startDate} → ${endDate}`;
    if (startDate) return `${startDate} → bugün`;
    if (endDate) return `Son ${days} gün · ${endDate}'ye kadar`;
    return `Son ${days} gün`;
  }, [days, startDate, endDate]);

  const load = async () => {
    const [s, d, mt] = await Promise.all([
      api.get("/reports/summary"),
      api.get(`/reports/daily?${query}`),
      api.get("/reports/monthly-trend?months=12"),
    ]);
    setSummary(s.data);
    setSeries(d.data.series || []);
    setMonthlyTrend(mt.data.series || []);
  };

  const loadTopCustomers = async () => {
    if (user?.role === "sales") return;
    const { data } = await api.get(`/reports/top-customers?month=${topMonth}&limit=5`);
    setTopCustomers(data.items || []);
  };

  const loadTopProducts = async () => {
    if (user?.role === "sales") return;
    try {
      const [m, y] = await Promise.all([
        api.get(`/reports/top-products?period=month`),
        api.get(`/reports/top-products?period=year`),
      ]);
      setTopProductsMonth(m.data.items || []);
      setTopProductsYear(y.data.items || []);
    } catch { /* silent */ }
  };

  const loadSalesPerformance = async () => {
    if (!isPrivileged) return;
    const { data } = await api.get(`/reports/sales-performance?month=${perfMonth}`);
    setSalesPerformance(data.items || []);
  };

  const loadPendingWaybillCustomers = async () => {
    if (!isPrivileged) return;
    try {
      const { data } = await api.get(`/reports/pending-waybill-customers`);
      setPendingWaybillCustomers(data.items || []);
    } catch { /* silent */ }
  };

  const openPwcBreakdown = async (customer) => {
    setPwcSelected(customer);
    setPwcBreakdown(null);
    setPwcLoading(true);
    try {
      const cid = customer.customer_id || "";
      const { data } = await api.get(`/reports/pending-waybill-customers/${encodeURIComponent(cid)}/breakdown`);
      setPwcBreakdown(data);
    } catch {
      setPwcBreakdown({ ready_to_waybill: { items: [], total: 0 }, in_production: { items: [], total: 0 }, in_warehouse_prep: { items: [], total: 0 } });
    } finally {
      setPwcLoading(false);
    }
  };

  const loadFinanceSummary = async () => {
    if (!isPrivileged) return;
    try {
      const { data } = await api.get(`/finance/summary?_ts=${Date.now()}`);
      setFinanceSummary(data);
    } catch { /* silent: finance is optional on Dashboard */ }
  };

  const loadDailyQuotes = async () => {
    if (!canSeeDailyQuotes) return;
    try {
      const { data } = await api.get(`/reports/daily-quotes-summary?_ts=${Date.now()}`);
      setDailyQuotes(data);
    } catch { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [query]);
  useEffect(() => { loadTopCustomers(); /* eslint-disable-next-line */ }, [topMonth]);
  useEffect(() => { loadTopProducts(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { loadSalesPerformance(); /* eslint-disable-next-line */ }, [perfMonth, isPrivileged]);
  useEffect(() => { loadFinanceSummary(); /* eslint-disable-next-line */ }, [isPrivileged]);
  useEffect(() => { loadDailyQuotes(); /* eslint-disable-next-line */ }, [canSeeDailyQuotes]);
  useEffect(() => { loadPendingWaybillCustomers(); /* eslint-disable-next-line */ }, [isPrivileged]);

  // ---- Silent auto-refresh (every 2 minutes) ----
  // Fetches all Dashboard data in the background so users see fresh numbers
  // without a full-page reload. Also refreshes when the tab becomes visible
  // after being hidden, so returning to the tab shows current data.
  useEffect(() => {
    let cancelled = false;
    const silentRefresh = async () => {
      if (document.hidden) return;
      try {
        await Promise.all([
          load(),
          loadTopCustomers(),
          loadTopProducts(),
          loadSalesPerformance(),
          loadFinanceSummary(),
          loadPendingWaybillCustomers(),
          loadDailyQuotes(),
        ]);
        if (!cancelled) setLastRefreshedAt(new Date());
      } catch { /* silent — next tick will retry */ }
    };
    const intervalId = setInterval(silentRefresh, 120_000);
    const onVisible = () => { if (!document.hidden) silentRefresh(); };
    document.addEventListener("visibilitychange", onVisible);
    // Prime the timestamp on first load
    setLastRefreshedAt(new Date());
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
    /* eslint-disable-next-line */
  }, [query, topMonth, perfMonth, isPrivileged]);

  const downloadFile = async (path, filename) => {
    const res = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const applyPreset = (n) => {
    setStartDate("");
    setEndDate("");
    setDays(n);
  };

  const resetRange = () => {
    setStartDate("");
    setEndDate("");
    setDays(30);
  };

  const usingDateRange = Boolean(startDate || endDate);

  const PwcGroup = ({ title, accent, rows, columns, testid, emptyLabel }) => (
    <div className="border-2 border-[#09090B]" data-testid={`${testid}-group`}>
      <div className={`flex items-center justify-between gap-3 px-3 py-2 border-b-2 border-[#09090B] bg-white`}>
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-block w-2 h-6 ${accent}`} />
          <div className="font-bold tracking-tight text-sm">{title}</div>
          <span className="text-[10px] font-mono text-neutral-500 uppercase">· {rows.length} kalem</span>
        </div>
        <div className="font-mono text-sm font-bold whitespace-nowrap" data-testid={`${testid}-total`}>
          {fmtCurrency(rows.reduce((s, r) => s + (Number(r.line_total) || 0), 0))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs font-mono text-neutral-500 py-4 text-center" data-testid={`${testid}-empty`}>{emptyLabel}</div>
      ) : (
        <div className="max-h-[260px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-neutral-100 border-b border-neutral-300 sticky top-0">
                {columns.map((c) => (
                  <th key={c.key} className={`font-mono uppercase text-[9px] px-2 py-1.5 ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.order_id}-${r.product_name}-${i}`} className="border-b border-neutral-100 last:border-b-0" data-testid={`${testid}-row-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={`px-2 py-1.5 ${c.align === "right" ? "text-right font-mono" : ""} ${c.bold ? "font-bold" : ""}`}>
                      {c.render ? c.render(r) : r[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <PageHeader
        kicker="Genel Bakış"
        title="Panel"
        actions={
          <div className="flex flex-wrap gap-2 items-center">
            {lastRefreshedAt && (
              <span
                className="hidden md:inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-neutral-500 border border-neutral-300 px-2 h-10"
                data-testid="dashboard-last-refreshed"
                title="Panel her 2 dakikada bir arka planda otomatik yenilenir"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#00A650] animate-pulse" />
                Güncel · {lastRefreshedAt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
            <label className="inline-flex items-center gap-2 border-2 border-[#09090B] bg-white px-3 h-10 cursor-pointer" data-testid="detailed-pdf-toggle-wrap">
              <input
                type="checkbox"
                checked={detailedPdf}
                onChange={(e) => setDetailedPdf(e.target.checked)}
                className="w-4 h-4 accent-[#09090B]"
                data-testid="detailed-pdf-toggle"
              />
              <span className="text-xs font-mono uppercase tracking-widest">Detaylı PDF</span>
            </label>
            {isPrivileged && (
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => downloadFile(`/reports/daily/csv?${query}`, "gunluk_rapor.csv")} data-testid="export-csv-btn">
              <FileDown size={16} className="mr-2" /> CSV
            </Button>
            )}
            <Button
              className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
              onClick={() => downloadFile(
                `/reports/daily/pdf?${query}${detailedPdf ? "&detailed=true" : ""}`,
                detailedPdf ? "gunluk_rapor_detayli.pdf" : "gunluk_rapor.pdf",
              )}
              data-testid="export-pdf-btn"
            >
              <Download size={16} className="mr-2" /> PDF
            </Button>
          </div>
        }
      />

      <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${isPrivileged ? "xl:grid-cols-[227px_260px_260px_155px_158px_158px]" : "xl:grid-cols-4"}`}>
        <Stat
          label="Bugünkü Ciro"
          value={fmtCurrency(summary?.today_subtotal)}
          testid="stat-today-total"
          sub={
              <div className="space-y-0.5 text-[10px] font-mono break-words">
                <div data-testid="stat-today-total-inc">KDV Dahil: {fmtCurrency(summary?.today_total)}</div>
                <div data-testid="stat-today-tax">KDV: {fmtCurrency(summary?.today_tax)}</div>
                <div className="text-neutral-500">{summary?.today_count || 0} sipariş</div>
              </div>
          }
        />
        {isPrivileged && (
          <Stat
            label="Aylık Ciro"
            value={fmtCurrency(summary?.monthly_subtotal)}
            testid="stat-monthly-total"
            sub={
              <div className="space-y-0.5 text-[10px] font-mono break-words">
                <div data-testid="stat-monthly-total-inc">KDV Dahil: {fmtCurrency(summary?.monthly_total)}</div>
                <div data-testid="stat-monthly-tax">KDV: {fmtCurrency(summary?.monthly_tax)}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-neutral-500">{summary?.monthly_count || 0} sipariş</span>
                  <ChangeBadge current={summary?.monthly_subtotal} previous={summary?.prev_monthly_subtotal}
                    label="Geçen aya göre" testid="stat-monthly-change" />
                </div>
              </div>
            }
          />
        )}
        {isPrivileged && (
          <Stat
            label="Yıllık Ciro"
            value={fmtCurrency(summary?.yearly_subtotal)}
            testid="stat-yearly-total"
            sub={
              <div className="space-y-0.5 text-[10px] font-mono break-words">
                <div data-testid="stat-yearly-total-inc">KDV Dahil: {fmtCurrency(summary?.yearly_total)}</div>
                <div data-testid="stat-yearly-tax">KDV: {fmtCurrency(summary?.yearly_tax)}</div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-neutral-500">{summary?.yearly_count || 0} sipariş</span>
                  <ChangeBadge current={summary?.yearly_subtotal} previous={summary?.prev_yearly_subtotal}
                    label="Geçen yıla göre" testid="stat-yearly-change" />
                </div>
              </div>
            }
          />
        )}
        <Stat label="Toplam Sipariş" value={summary?.orders_count ?? "-"} testid="stat-orders-count" />
        <Stat label="Dönüşüm Oranı" value={`${summary?.conversion_rate ?? 0}%`} testid="stat-conversion" sub={`${summary?.quotes_count || 0} teklif`} />
        <Stat label="Aktif İşler" value={(summary?.warehouse_pending || 0) + (summary?.production_pending || 0)} testid="stat-active-jobs" sub={`Depo: ${summary?.warehouse_pending || 0} kalem · Üretim: ${summary?.production_pending || 0} kalem`} />
      </div>

      {(isPrivileged || canSeeDailyQuotes) && (dailyQuotes || (isPrivileged && financeSummary)) && (
        <div className="space-y-4" data-testid="dashboard-finance-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {dailyQuotes && canSeeDailyQuotes && (
              <div className="bg-white border-2 border-[#09090B] border-l-[6px] border-l-[#0055FF] p-5" data-testid="dashboard-daily-quotes-widget">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  <FileText size={12} /> Günlük Teklif Özeti
                </div>
                <div className="mt-2 flex items-baseline gap-3 flex-wrap">
                  <div className="text-3xl font-mono tracking-tight font-bold" data-testid="daily-quotes-count">
                    {dailyQuotes.quote_count}
                  </div>
                  <div className="text-xs font-mono text-neutral-500">bugün oluşturulan teklif</div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] font-mono">
                  <div className="border-2 border-[#09090B] p-2" data-testid="daily-quotes-subtotal">
                    <div className="text-[9px] uppercase text-neutral-500">KDV Hariç</div>
                    <div className="font-bold text-sm">{fmtCurrency(dailyQuotes.subtotal)}</div>
                  </div>
                  <div className="border-2 border-[#09090B] p-2" data-testid="daily-quotes-total">
                    <div className="text-[9px] uppercase text-neutral-500">KDV Dahil</div>
                    <div className="font-bold text-sm">{fmtCurrency(dailyQuotes.total)}</div>
                  </div>
                </div>
                <div className="mt-3 space-y-1 text-xs font-mono">
                  <div className="flex items-center justify-between border-2 border-[#09090B] bg-[#09090B] text-white px-2 py-1" data-testid="daily-quotes-conversion">
                    <span className="text-[10px] uppercase tracking-wide">Tutar Dönüşüm Oranı</span>
                    <span className="font-bold text-sm">%{dailyQuotes.conversion_rate} <span className="text-[9px] text-neutral-300 font-normal">(tutar bazlı)</span></span>
                  </div>
                  <div className="flex items-center justify-between border-2 border-[#09090B] px-2 py-1" data-testid="daily-quotes-conversion-count">
                    <span className="text-[10px] uppercase tracking-wide text-neutral-700">Adet Dönüşüm Oranı</span>
                    <span className="font-bold text-sm">%{dailyQuotes.conversion_rate_count ?? 0} <span className="text-[9px] text-neutral-500 font-normal">(adet bazlı)</span></span>
                  </div>
                </div>
                <div className="mt-1 text-[10px] font-mono text-neutral-500">
                  {fmtCurrency(dailyQuotes.converted_subtotal || 0)} / {fmtCurrency(dailyQuotes.subtotal)} ·  {dailyQuotes.converted_count}/{dailyQuotes.quote_count} teklif siparişe dönüştü
                </div>
              </div>
            )}
            {isPrivileged && financeSummary && (
              <div className="bg-white border-2 border-[#09090B] border-l-[6px] border-l-[#FF9500] p-5" data-testid="dashboard-finance-portfolio">
                <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
                  <TrendingUp size={12} /> Portföydeki Çek / Senet
                </div>
                <div className="mt-2 text-3xl font-mono tracking-tight font-bold">{fmtCurrency(financeSummary.portfolio_total)}</div>
                <div className="mt-1 text-xs font-mono text-neutral-500">{financeSummary.portfolio_cheques} çek · {financeSummary.portfolio_notes} senet</div>
              </div>
            )}
          </div>
        </div>
      )}

      {isPrivileged && (
      <div className="bg-white border-2 border-[#09090B] p-6" data-testid="daily-chart-card">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500">Günlük Sipariş Değeri</div>
            <div className="text-xl font-bold tracking-tight" data-testid="range-label">{rangeLabel}</div>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            {/* Preset chips */}
            <div className="flex gap-1">
              {[7, 30, 90].map((n) => (
                <button
                  key={n}
                  onClick={() => applyPreset(n)}
                  data-testid={`range-${n}`}
                  className={`px-3 h-10 text-xs font-mono border-2 border-[#09090B] ${!usingDateRange && days === n ? "bg-[#09090B] text-white" : "bg-white hover:bg-neutral-100"}`}
                >{n}G</button>
              ))}
            </div>

            {/* Explicit date range */}
            <div className="flex items-end gap-2">
              <div>
                <Label htmlFor="start-date" className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Başlangıç</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  max={endDate || today()}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="report-start-date"
                  className="h-10 w-40 rounded-none border-2 border-[#09090B] font-mono text-xs focus-visible:ring-0"
                />
              </div>
              <div>
                <Label htmlFor="end-date" className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">Bitiş</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  max={today()}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="report-end-date"
                  className="h-10 w-40 rounded-none border-2 border-[#09090B] font-mono text-xs focus-visible:ring-0"
                />
              </div>
              {usingDateRange && (
                <Button
                  variant="outline"
                  onClick={resetRange}
                  data-testid="reset-range"
                  className="h-10 rounded-none border-2 border-[#09090B]"
                  title="Aralığı sıfırla"
                >
                  <RotateCcw size={14} className="mr-1" /> Sıfırla
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series}>
              <CartesianGrid stroke="#D4D4D8" strokeDasharray="0" vertical={false} />
              <XAxis dataKey="date" stroke="#09090B" fontSize={11} tickLine={false} axisLine={{ stroke: "#09090B" }} />
              <YAxis stroke="#09090B" fontSize={11} tickLine={false} axisLine={{ stroke: "#09090B" }} />
              <Tooltip
                contentStyle={{ background: "#fff", border: "2px solid #09090B", borderRadius: 0, fontFamily: "monospace" }}
                formatter={(v) => fmtCurrency(v)}
              />
              <Legend wrapperStyle={{ fontFamily: "monospace", fontSize: 11 }} />
              <Bar dataKey="subtotal" name="KDV Hariç" fill="#A1A1AA" />
              <Bar dataKey="total" name="KDV Dahil" fill="#09090B" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}

      <div className={`grid grid-cols-1 gap-4 ${isPrivileged ? "lg:grid-cols-3" : ""}`}>
        {isPrivileged && (
        <div className="bg-white border-2 border-[#09090B] p-6 lg:col-span-2" data-testid="monthly-trend-card">
          <div className="flex items-center gap-3 mb-4">
            <TrendingUp size={20} className="text-[#0055FF]" />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Son 12 Ay · KDV Hariç</div>
              <div className="text-lg font-bold tracking-tight">Aylık Ciro Trendi</div>
            </div>
          </div>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyTrend} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#D4D4D8" strokeDasharray="0" vertical={false} />
                <XAxis dataKey="month" stroke="#09090B" fontSize={10} tickLine={false} axisLine={{ stroke: "#09090B" }} tickFormatter={(v) => (v || "").slice(2)} />
                <YAxis stroke="#09090B" fontSize={10} tickLine={false} axisLine={{ stroke: "#09090B" }} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "2px solid #09090B", borderRadius: 0, fontFamily: "monospace" }}
                  formatter={(v, key, item) => [`${fmtCurrency(v)} · ${item?.payload?.count ?? 0} sipariş`, "KDV Hariç Ciro"]}
                  labelFormatter={(l) => `Ay: ${l}`}
                />
                <Bar dataKey="subtotal" fill="#0055FF" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <QuarterlySalesChart />
        </div>
        )}

        {/* Top customers — satış rolü için gizli */}
        {user?.role !== "sales" && (
        <div className="bg-white border-2 border-[#09090B] p-6" data-testid="top-customers-card">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <Trophy size={20} className="text-[#FF9500] shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Aylık</div>
                <div className="text-lg font-bold tracking-tight truncate">En Çok Sipariş Verenler</div>
              </div>
            </div>
            <select
              value={topMonth}
              onChange={(e) => setTopMonth(e.target.value)}
              data-testid="top-customers-month-select"
              className="h-8 rounded-none border-2 border-[#09090B] font-mono text-xs px-2 bg-white focus:outline-none"
            >
              {monthOptions.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
          {topCustomers.length === 0 ? (
            <div className="text-sm font-mono text-neutral-500 py-6 text-center" data-testid="top-customers-empty">
              Bu ayda sipariş yok
            </div>
          ) : (
            <div className="divide-y divide-neutral-200">
              {topCustomers.map((c, i) => (
                <div
                  key={c.customer_id || i}
                  className="flex items-center gap-3 py-2.5"
                  data-testid={`top-customer-${i}`}
                >
                  <div className={`w-7 h-7 flex items-center justify-center font-mono font-bold text-xs border-2 border-[#09090B] ${i === 0 ? "bg-[#FF9500] text-white" : "bg-white"}`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate" data-testid={`top-customer-name-${i}`}>{c.customer_name}</div>
                    <div className="text-[10px] font-mono text-neutral-500 space-y-0.5">
                      <div data-testid={`top-customer-subtotal-${i}`}>KDV Hariç: {fmtCurrency(c.subtotal)}</div>
                      <div data-testid={`top-customer-tax-${i}`}>KDV: {fmtCurrency(c.tax)}</div>
                      <div>{c.count} sipariş</div>
                    </div>
                  </div>
                  <div className="text-right font-mono text-sm font-bold" data-testid={`top-customer-total-${i}`}>
                    {fmtCurrency(c.total)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}
      </div>

      {isPrivileged && <HseExpiringWidget />}

      {/* Top products (month + year) — satış rolü için gizli */}
      {user?.role !== "sales" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopProductsCard
            kicker="Aylık"
            title="En Çok Satılan Ürünler"
            items={topProductsMonth}
            emptyLabel="Bu ayda satılan ürün yok"
            testid="top-products-month"
            period="month"
          />
          <TopProductsCard
            kicker="Yıllık"
            title="En Çok Satılan Ürünler"
            items={topProductsYear}
            emptyLabel="Bu yılda satılan ürün yok"
            testid="top-products-year"
            period="year"
          />
        </div>
      )}

      {isPrivileged && (
        <div className="bg-white border-2 border-[#09090B] p-6" data-testid="pending-waybill-customers-card">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <Truck size={20} className="text-[#FF9500] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Yalnızca Yönetici</div>
              <div className="text-lg font-bold tracking-tight">İrsaliyesi Kesilmemiş Müşteriler</div>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#09090B] font-mono uppercase text-[10px] tracking-widest" data-testid="pending-waybill-customers-count">
                {pendingWaybillCustomers.length} müşteri
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 border-2 border-[#FF9500] text-[#FF9500] font-mono uppercase text-[10px] tracking-widest" data-testid="pending-waybill-customers-total">
                Toplam: {fmtCurrency(pendingWaybillCustomers.reduce((s, c) => s + (Number(c.total) || 0), 0))}
              </div>
            </div>
          </div>
          {pendingWaybillCustomers.length === 0 ? (
            <div className="text-sm font-mono text-neutral-500 py-6 text-center" data-testid="pending-waybill-customers-empty">
              İrsaliyesi kesilmemiş müşteri yok
            </div>
          ) : (
            <div className="border-2 border-[#09090B] max-h-[460px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0">
                  <tr className="bg-neutral-100 border-b-2 border-[#09090B]">
                    <th className="font-mono uppercase text-[10px] text-left px-3 py-2 w-10">#</th>
                    <th className="font-mono uppercase text-[10px] text-left px-3 py-2">Müşteri</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">Kesilmemiş İrsaliye</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">Toplam Tutar</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingWaybillCustomers.map((c, i) => (
                    <tr
                      key={c.customer_id || c.customer_name || i}
                      data-testid={`pending-waybill-customer-row-${i}`}
                      className="border-b border-neutral-200 last:border-b-0 cursor-pointer hover:bg-neutral-50 transition-colors"
                      onClick={() => openPwcBreakdown(c)}
                      title="Detayları aç"
                    >
                      <td className="px-3 py-2 font-mono text-xs">
                        <span className={`inline-flex w-6 h-6 items-center justify-center border-2 border-[#09090B] font-bold ${i === 0 ? "bg-[#FF9500] text-white" : "bg-white"}`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold" data-testid={`pending-waybill-customer-name-${i}`}>{c.customer_name}</td>
                      <td className="px-3 py-2 text-right font-mono" data-testid={`pending-waybill-customer-count-${i}`}>{c.count}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold" data-testid={`pending-waybill-customer-total-${i}`}>{fmtCurrency(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {isPrivileged && (
        <div className="bg-white border-2 border-[#09090B] p-6" data-testid="sales-performance-card">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <UsersIcon size={20} className="text-[#0055FF] shrink-0" />
              <div className="min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">Yalnızca Yönetici</div>
                <div className="text-lg font-bold tracking-tight">Aylık Satış Performansı</div>
              </div>
            </div>
            <select
              value={perfMonth}
              onChange={(e) => setPerfMonth(e.target.value)}
              data-testid="sales-performance-month-select"
              className="h-8 rounded-none border-2 border-[#09090B] font-mono text-xs px-2 bg-white focus:outline-none"
            >
              {monthOptions.map((o) => (
                <option key={o.key} value={o.key}>{o.label}</option>
              ))}
            </select>
          </div>
          {salesPerformance.length === 0 ? (
            <div className="text-sm font-mono text-neutral-500 py-6 text-center" data-testid="sales-performance-empty">
              Bu ayda oluşturulmuş sipariş yok
            </div>
          ) : (
            <div className="border-2 border-[#09090B]">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-100 border-b-2 border-[#09090B]">
                    <th className="font-mono uppercase text-[10px] text-left px-3 py-2 w-10">#</th>
                    <th className="font-mono uppercase text-[10px] text-left px-3 py-2">Satış Personeli</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">Sipariş</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">KDV Hariç</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">KDV</th>
                    <th className="font-mono uppercase text-[10px] text-right px-3 py-2">Toplam</th>
                  </tr>
                </thead>
                <tbody>
                  {salesPerformance.map((r, i) => (
                    <tr key={r.user_id || i} data-testid={`sales-perf-row-${i}`} className="border-b border-neutral-200 last:border-b-0">
                      <td className="px-3 py-2 font-mono text-xs">
                        <span className={`inline-flex w-6 h-6 items-center justify-center border-2 border-[#09090B] font-bold ${i === 0 ? "bg-[#0055FF] text-white" : "bg-white"}`}>
                          {i + 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-semibold" data-testid={`sales-perf-name-${i}`}>{r.user_name}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.count}</td>
                      <td className="px-3 py-2 text-right font-mono text-neutral-500">{fmtCurrency(r.subtotal)}</td>
                      <td className="px-3 py-2 text-right font-mono text-neutral-500">{fmtCurrency(r.tax)}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold" data-testid={`sales-perf-total-${i}`}>{fmtCurrency(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border-2 border-[#09090B] border-l-[6px] border-l-[#0055FF] p-6">
          <div className="flex items-center gap-3">
            <WhIcon size={22} className="text-[#0055FF]" />
            <div className="text-lg font-bold">Depo Sevk Kuyruğu</div>
          </div>
          <div className="mt-2 text-4xl font-mono font-bold">{summary?.warehouse_pending || 0}</div>
          <p className="mt-2 text-sm text-neutral-500">Stokta bulunan ürünler için hazırlık bekleyen siparişler.</p>
        </div>
        <div className="bg-white border-2 border-[#09090B] border-l-[6px] border-l-[#FF3B30] p-6">
          <div className="flex items-center gap-3">
            <Factory size={22} className="text-[#FF3B30]" />
            <div className="text-lg font-bold">Üretim Emri</div>
          </div>
          <div className="mt-2 text-4xl font-mono font-bold">{summary?.production_pending || 0}</div>
          <p className="mt-2 text-sm text-neutral-500">Stokta yetersiz kalan ürünler için üretime iletilen siparişler.</p>
        </div>
      </div>

      <Dialog open={!!pwcSelected} onOpenChange={(v) => { if (!v) { setPwcSelected(null); setPwcBreakdown(null); } }}>
        <DialogContent
          className="rounded-none border-2 border-[#09090B] bg-white max-w-5xl max-h-[88vh] overflow-y-auto"
          data-testid="pwc-breakdown-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {pwcSelected?.customer_name || "Müşteri"}
            </DialogTitle>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500">
              Bekleyen Siparişler · Detaylı Döküm
            </div>
          </DialogHeader>
          {pwcLoading ? (
            <div className="py-10 text-center text-sm font-mono text-neutral-500">Yükleniyor…</div>
          ) : pwcBreakdown ? (
            <div className="space-y-4 mt-2">
              <PwcGroup
                title="İrsaliyesi Kesilecek"
                accent="bg-[#FF9500]"
                rows={pwcBreakdown.ready_to_waybill?.items || []}
                emptyLabel="İrsaliyesi kesilecek kalem yok"
                testid="pwc-ready"
                columns={[
                  { key: "order_number", label: "Sipariş", bold: true },
                  { key: "product_name", label: "Ürün" },
                  { key: "due_date", label: "Termin", render: (r) => r.due_date ? fmtDate(r.due_date) : "—" },
                  { key: "available_qty", label: "Hazır", align: "right", render: (r) => `${Number(r.available_qty).toLocaleString("tr-TR")} ${r.unit}` },
                  { key: "line_total", label: "Tutar", align: "right", bold: true, render: (r) => fmtCurrency(r.line_total) },
                ]}
              />
              <PwcGroup
                title="Üretimde"
                accent="bg-[#FF3B30]"
                rows={pwcBreakdown.in_production?.items || []}
                emptyLabel="Üretimde kalem yok"
                testid="pwc-production"
                columns={[
                  { key: "order_number", label: "Sipariş", bold: true },
                  { key: "product_name", label: "Ürün" },
                  { key: "due_date", label: "Termin", render: (r) => r.due_date ? fmtDate(r.due_date) : "—" },
                  { key: "progress", label: "Üretilen / Toplam", align: "right", render: (r) => `${Number(r.produced_qty).toLocaleString("tr-TR")} / ${Number(r.required_qty).toLocaleString("tr-TR")} ${r.unit}` },
                  { key: "remaining_qty", label: "Kalan", align: "right", render: (r) => `${Number(r.remaining_qty).toLocaleString("tr-TR")} ${r.unit}` },
                  { key: "line_total", label: "Tutar", align: "right", bold: true, render: (r) => fmtCurrency(r.line_total) },
                ]}
              />
              <PwcGroup
                title="Depoda Hazırlanacak"
                accent="bg-[#0055FF]"
                rows={pwcBreakdown.in_warehouse_prep?.items || []}
                emptyLabel="Depoda hazırlanacak kalem yok"
                testid="pwc-warehouse"
                columns={[
                  { key: "order_number", label: "Sipariş", bold: true },
                  { key: "product_name", label: "Ürün" },
                  { key: "due_date", label: "Termin", render: (r) => r.due_date ? fmtDate(r.due_date) : "—" },
                  { key: "progress", label: "Hazır / Toplam", align: "right", render: (r) => `${Number(r.ready_qty).toLocaleString("tr-TR")} / ${Number(r.quantity).toLocaleString("tr-TR")} ${r.unit}` },
                  { key: "remaining_qty", label: "Kalan", align: "right", render: (r) => `${Number(r.remaining_qty).toLocaleString("tr-TR")} ${r.unit}` },
                  { key: "line_total", label: "Tutar", align: "right", bold: true, render: (r) => fmtCurrency(r.line_total) },
                ]}
              />
              <div className="flex items-center justify-between gap-3 px-3 py-2 border-2 border-[#09090B] bg-neutral-50 font-mono text-xs uppercase tracking-widest" data-testid="pwc-grand-total">
                <span>Genel Toplam</span>
                <span className="font-bold text-base">
                  {fmtCurrency(
                    (pwcBreakdown.ready_to_waybill?.total || 0) +
                    (pwcBreakdown.in_production?.total || 0) +
                    (pwcBreakdown.in_warehouse_prep?.total || 0)
                  )}
                </span>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm font-mono text-neutral-500">Veri yok</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
